import { ChevronDown, GroupPeople } from 'assets/icons';
import EmployeeAvatar from 'components/assign/EmployeeAvatar';
import useText from 'hooks/useText';
import { useEffect, useRef, useState } from 'react';

const fullName = (person) => `${person.firstname} ${person.lastname}`.trim();

/**
 * Whose tasks an agency manager looks at: boxes for the whole team, yourself
 * and each colleague. The choice applies when the list closes. `value`:
 * 'team' or a list of ids.
 */
const PeopleSelect = ({ team, meId, value, onChange }) => {
  const { text } = useText('tasks');
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(value);
  const box = useRef(null);
  const everyone = picked === 'team';
  const ids = everyone ? team.map((person) => person.id) : picked;

  useEffect(() => setPicked(value), [value]);
  const apply = () => {
    setOpen(false);
    const next = everyone || (team.length > 1 && ids.length === team.length) ? 'team' : ids.length ? ids : [meId];
    if (JSON.stringify(next) !== JSON.stringify(value)) onChange(next);
  };
  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (!box.current?.contains(event.target)) apply();
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }); // the listener follows the choice being made

  const toggle = (id) => setPicked(ids.includes(id) ? ids.filter((other) => other !== id) : [...ids, id]);
  const me = team.find((person) => person.id === meId);
  const others = team.filter((person) => person.id !== meId);
  const chosen = team.filter((person) => ids.includes(person.id));
  const label = everyone
    ? text('peopleTeam')
    : chosen.length === 1 && chosen[0].id === meId
      ? text('peopleMe', { name: chosen[0].firstname })
      : chosen.map((person) => (person.id === meId ? text('peopleMeShort') : person.firstname)).join(', ');
  const row = (key, checked, onToggle, avatar, name) => (
    <label key={key} className="flex cursor-pointer items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100">
      <input type="checkbox" className="checkbox-primary" checked={checked} onChange={onToggle} />
      {avatar}
      <span className="truncate">{name}</span>
    </label>
  );

  return (
    <div ref={box} className="relative shrink-0">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => (open ? apply() : setOpen(true))}
        className="flex h-10 max-w-[280px] items-center gap-2 rounded-md border border-gray-200 bg-white pl-2.5 pr-3 text-sm text-gray-700 transition-colors hover:bg-gray-50"
      >
        {everyone ? (
          <span className="flex size-6 items-center justify-center rounded-full bg-gray-100">
            <GroupPeople wh={14} color="#374151" />
          </span>
        ) : (
          <span className="flex -space-x-1.5">
            {chosen.slice(0, 3).map((person) => (
              <span key={person.id} className="rounded-full ring-2 ring-white">
                <EmployeeAvatar employee={person} />
              </span>
            ))}
          </span>
        )}
        <span className="truncate">{label}</span>
        <ChevronDown wh={16} color="#9CA3AF" />
      </button>
      {open && (
        <div className="absolute right-0 top-full z-30 mt-1 w-64 rounded-lg border border-gray-100 bg-white py-1 shadow-lg">
          {row(
            'team',
            everyone,
            () => setPicked(everyone ? [meId] : 'team'),
            <span className="flex size-6 items-center justify-center rounded-full bg-gray-100">
              <GroupPeople wh={14} color="#374151" />
            </span>,
            text('peopleTeam'),
          )}
          <div className="my-1 border-t border-gray-100" />
          {me && row(me.id, ids.includes(me.id), () => toggle(me.id), <EmployeeAvatar employee={me} />, text('peopleMe', { name: me.firstname }))}
          {others.map((person) =>
            row(person.id, ids.includes(person.id), () => toggle(person.id), <EmployeeAvatar employee={person} />, fullName(person)),
          )}
        </div>
      )}
    </div>
  );
};

export default PeopleSelect;
